import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import Reset from "./Reset";
import "react-toastify/dist/ReactToastify.css";
// Hero illustration is served from /public/assets/login/ rather
// than imported, so the 2.6 MB SVG sits in browser cache as a
// static asset instead of inlining into the JS bundle. Vite
// rewrites ``import.meta.env.BASE_URL`` for non-root deploys.
const loginImg = `${import.meta.env.BASE_URL}assets/login/loginImg.svg`;
// Brand wordmark — same transparent-PNG the navbar + footer use,
// shown above the form on mobile only (md:hidden) since the
// desktop layout already carries the brand via the hero
// illustration on the left half. ``app-logo`` wires the per-theme
// filter chain (light keeps brand cyan, dark inverts to white,
// etc.).
import logo from "../../assets/plumescript-logo-removebg.png";
import {
  faEye,
  faEyeSlash,
  faShieldHalved,
  faGlobe,
  faPenNib,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer } from "react-toastify";
import { useT } from "../../i18n/I18nContext";
import LocalePicker from "../../i18n/LocalePicker";
import { useAuth } from "../../auth/AuthContext";
import { logger } from "../../util/logger";
import Seo from "../../seo/Seo";

const Login = () => {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, status, signIn } = useAuth();

  // Honour ``?next=`` so RequireAuth redirects round-trip back to the
  // originally requested page after a successful sign-in. Reject anything
  // that isn't a same-origin path so the query param can't be abused.
  const nextParam = new URLSearchParams(location.search).get("next");
  const nextTarget =
    nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")
      ? nextParam
      : "/";

  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword((v) => !v);
  const openPasswordResetForm = () => {
    // The Reset panel must not call ``useNavigate`` itself —
    // react-toastify portals it under whichever <ToastContainer>
    // mounted first, which can be the one in main.jsx (a sibling
    // of <RouterProvider>, so router context isn't available
    // there). We own the routing here and pass it down.
    const id = toast(
      <Reset
        onDismiss={() => toast.dismiss(id)}
        onRegisterClick={() => navigate("/register")}
      />,
      { autoClose: false },
    );
  };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (status === "signedIn" && user) {
      navigate(nextTarget, { replace: true });
    }
  }, [status, user, navigate, nextTarget]);

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password || submitting) return;
    setSubmitting(true);
    try {
      await signIn({ email: trimmedEmail, password, remember });
    } catch (err) {
      logger.warn("login_failed", err?.response?.data?.error ?? err?.message);
      toast.error(
        err?.response?.data?.error === "invalid_credentials"
          ? t("auth.invalid_credentials")
          : err?.message ?? t("auth.signin_failed"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo title={t("nav.login")} path="/login" noindex />
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <div className="relative min-h-screen flex flex-col md:flex-row bg-surfaceAlt">
        {/* Discrete locale switch in the top-right corner — keeps
            the form minimal but lets the user flip EN/FR before
            signing in. */}
        <LocalePicker className="absolute top-3 right-4 z-20" />

        {/* Hero illustration — desktop only. Mobile drops it to keep
            the viewport for the form (the SVG is decorative;
            collapsing it on mobile saves a 2.6 MB asset and a screen
            of scrolling before the inputs appear). */}
        <div className="hidden md:block md:w-1/2">
          {/* Decorative hero acts as a "back to landing" affordance
              — the brand wordmark/link in the header is the
              keyboard path for the same navigation. */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
          <img
            src={loginImg}
            alt=""
            onClick={() => navigate("/")}
            className="cursor-pointer object-cover w-full h-screen"
          />
        </div>

        {/* Form column. Mobile-first: vertically centred, padded
            for thumb-reach (px-5 = 20px not 40px), max-w-sm caps
            the form on tablet so inputs don't span absurdly wide.
            ``md:bg-surface`` keeps the desktop two-column look
            (form column reads as the right half of the page);
            on mobile the parent's bg-surfaceAlt shows through so
            the form-card below can sit on a different shade and
            feel anchored instead of floating on a flat surface. */}
        <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-10 md:py-12 md:bg-surface">
          <div className="w-full max-w-sm">
            {/* Mobile-only brand mark. Login has only two inputs +
                a checkbox row, so the page can carry a generously
                sized logo without crowding the form. Desktop hides
                this since the hero illustration on the left
                already brands the page. */}
            <Link
              to="/"
              aria-label="Plumescript"
              className="md:hidden flex justify-center mb-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-md"
            >
              <img
                src={logo}
                alt=""
                aria-hidden="true"
                className="app-logo h-20 w-auto"
              />
            </Link>

            {/* Form card. Mobile gets the full card chrome
                (rounded-2xl + border + shadow-soft) so the form
                sits as a defined object on the surfaceAlt page
                rather than a transparent block of floating inputs.
                ``md:bg-transparent md:border-0 md:shadow-none
                md:p-0`` reverts on desktop, where the form fills
                the right column and doesn't need its own card. */}
            <div className="bg-surface border border-border rounded-2xl shadow-soft p-6 sm:p-8 md:bg-transparent md:border-0 md:shadow-none md:p-0">
              <div className="mb-7 text-center">
                <h1 className="text-2xl sm:text-3xl font-bold text-text">
                  {t("auth.login.title")}
                </h1>
                <p className="mt-2 text-sm text-secondText">
                  {t("auth.login.subtitle")}
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {/* Field labels are sr-only — the visual cue lives
                  in the placeholder (drops the stacked label row
                  that pushed the form below the fold on small
                  phones). The label tag stays in the DOM so screen
                  readers still announce each input. */}
              <div>
                <label htmlFor="email" className="sr-only">
                  {t("auth.email_label")}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="w-full px-4 py-3 rounded-full border border-border bg-surface md:bg-surfaceAlt text-text text-[16px]/[24px] placeholder:text-secondText/70 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition"
                  placeholder={t("auth.email_label")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- focus is intentional on this modal/form's primary input
                  autoFocus
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="sr-only">
                  {t("auth.password_label")}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className="w-full px-4 py-3 pr-12 rounded-full border border-border bg-surface md:bg-surfaceAlt text-text text-[16px]/[24px] placeholder:text-secondText/70 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition"
                    placeholder={t("auth.password_label")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center px-4 text-secondText hover:text-text transition"
                    aria-label={
                      showPassword
                        ? t("auth.hide_password")
                        : t("auth.show_password")
                    }
                    onClick={togglePasswordVisibility}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              {/* Remember + forgot on one row. On very narrow screens
                  the row stays single-line — both elements use small
                  text and the row has gap, not absolute pinning. */}
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="remember" className="flex items-center text-sm text-secondText cursor-pointer">
                  <input
                    type="checkbox"
                    id="remember"
                    name="remember"
                    className="mr-2 w-4 h-4 accent-primary"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  {t("auth.remember_me")}
                </label>
                <button
                  type="button"
                  onClick={openPasswordResetForm}
                  className="text-sm text-primary hover:underline"
                >
                  {t("auth.forgot_password")}
                </button>
              </div>

              <button
                type="submit"
                disabled={submitting || !email.trim() || !password}
                className="app-button-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? t("common.loading") : t("auth.login_cta")}
              </button>
            </form>

              <p className="mt-6 text-center text-sm text-secondText">
                {t("auth.no_account")}{" "}
                <Link to="/register" className="text-primary font-medium hover:underline">
                  {t("auth.sign_up")}
                </Link>
              </p>
            </div>

            {/* Reassurance strip — three quiet chips that ground
                the page without selling anything. Same visual
                rhythm as the EmptyState medallions on /browse and
                /collaboration so it reads as part of the app
                vocabulary. Shown on every viewport now (was
                mobile-only) so the desktop right column doesn't
                feel like dead space below the form either. */}
            <ul className="mt-8 grid grid-cols-3 gap-3 text-center">
              {[
                { icon: faShieldHalved, label: t("auth.feature.secure") },
                { icon: faGlobe, label: t("auth.feature.interactive") },
                { icon: faPenNib, label: t("auth.feature.friendly") },
              ].map((f) => (
                <li
                  key={f.label}
                  className="flex flex-col items-center gap-1.5"
                >
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/10 text-primary">
                    <FontAwesomeIcon icon={f.icon} className="text-sm" />
                  </span>
                  <span className="text-[11px] text-secondText leading-tight">
                    {f.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
